import React, { useState, useEffect, useCallback } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Sync as SyncIcon,
  CheckCircle as OkIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  CloudUpload as UploadIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import api from '../../services/api';

interface Activity {
  type: string;
  message: string;
  description?: string;
  status: string;
  timestamp: string;
}

// Scheduled / routine jobs the system runs on its own
const ROUTINE_TASKS = [
  {
    title: 'Nightly SIS Data Sync',
    schedule: 'Every day at 02:00',
    detail: 'Pulls all schools, enrolment, teachers & attendance from SIS',
  },
];

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (isNaN(then)) return '';
  const diff = Date.now() - then;
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

const statusChip = (status: string) => {
  const s = (status || '').toLowerCase();
  if (s.includes('fail') || s.includes('error'))
    return <Chip size="small" color="error" label="Failed" />;
  if (s.includes('progress') || s.includes('running'))
    return <Chip size="small" color="warning" label="Running" />;
  return <Chip size="small" color="success" label="Done" />;
};

const activityIcon = (type: string) => {
  if (type.includes('comprehensive')) return <UploadIcon fontSize="small" />;
  if (type.includes('weekly') || type.includes('assessment'))
    return <AssessmentIcon fontSize="small" />;
  return <SyncIcon fontSize="small" />;
};

const NotificationCenter: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/sis-integration/real-recent-activity/');
      const list = res.data?.data?.activities || [];
      setActivities(Array.isArray(list) ? list.slice(0, 10) : []);
    } catch {
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 5 * 60 * 1000); // refresh every 5 min
    return () => clearInterval(id);
  }, [load]);

  const failedCount = activities.filter((a) =>
    (a.status || '').toLowerCase().match(/fail|error/)
  ).length;
  const badgeCount = failedCount > 0 ? failedCount : activities.length;

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Activity & scheduled tasks">
        <IconButton
          color="inherit"
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
            load();
          }}
        >
          <Badge
            badgeContent={badgeCount}
            color={failedCount > 0 ? 'error' : 'primary'}
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ sx: { width: 380, maxHeight: 520 } }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Activity & Tasks
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Recent system activity and scheduled routines
          </Typography>
        </Box>
        <Divider />

        {/* Scheduled routine tasks */}
        <Box sx={{ px: 2, pt: 1.5 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ fontWeight: 700 }}
          >
            Scheduled Routines
          </Typography>
        </Box>
        <List dense disablePadding>
          {ROUTINE_TASKS.map((t) => (
            <ListItem key={t.title}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <ScheduleIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={t.title}
                secondary={`${t.schedule} — ${t.detail}`}
                primaryTypographyProps={{ fontWeight: 600, variant: 'body2' }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 1 }} />

        {/* Recent activity */}
        <Box sx={{ px: 2 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ fontWeight: 700 }}
          >
            Recent Activity
          </Typography>
        </Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : activities.length === 0 ? (
          <Box sx={{ px: 2, py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No recent activity recorded.
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding sx={{ pb: 1 }}>
            {activities.map((a, i) => {
              const failed = (a.status || '')
                .toLowerCase()
                .match(/fail|error/);
              return (
                <ListItem key={i} alignItems="flex-start">
                  <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                    {failed ? (
                      <ErrorIcon fontSize="small" color="error" />
                    ) : (
                      <OkIcon fontSize="small" color="success" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 1,
                        }}
                      >
                        <span>{a.message}</span>
                        {statusChip(a.status)}
                      </Box>
                    }
                    secondary={
                      <>
                        {a.description && (
                          <Typography variant="caption" display="block">
                            {a.description}
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {timeAgo(a.timestamp)}
                        </Typography>
                      </>
                    }
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Menu>
    </>
  );
};

export default NotificationCenter;
